import { apiClient } from '@/api/client';

// Classification types
export type ContentTheme =
  | 'AI'
  | 'Engineering'
  | 'Product'
  | 'Design'
  | 'Startups'
  | 'Business'
  | 'Tools';
export type ContentNature =
  | 'News'
  | 'Opinion'
  | 'Tutorial'
  | 'Analysis'
  | 'Announcement'
  | 'Curation'
  | 'Digest';
export type ContentDepth = 'Shallow' | 'Moderate' | 'Deep';
export type ContentTimeliness = 'Breaking' | 'Time-sensitive' | 'Evergreen';
export type ContentOriginality = 'Primary source' | 'Commentary' | 'Repost' | 'Curation';
export type TagSource = 'Extracted' | 'Inferred';
export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SubjectEntity {
  name: string;
  type: 'person' | 'company' | 'product' | 'organization' | 'technology';
}

export interface ClassificationConfidence {
  theme?: number;
  nature?: number;
  depth?: number;
  timeliness?: number;
  originality?: number;
  authority?: number;
  density?: number;
}

// Types matching backend
export interface ContentRevision {
  source: 'AI' | 'HUMAN';
  at: string; // ISO timestamp
  by?: string; // user_id for HUMAN revisions
  data: {
    title?: string;
    summary?: string;
    category?: string;
    author?: string;
    tags?: string[]; // tag UUIDs
    // Classification data
    theme?: ContentTheme;
    themeSuggested?: string;
    nature?: ContentNature;
    depth?: ContentDepth;
    timeliness?: ContentTimeliness;
    originality?: ContentOriginality;
    authorityScore?: number;
    densityScore?: number;
    primarySubject?: SubjectEntity;
    mentions?: SubjectEntity[];
    [key: string]: unknown;
  };
}

export interface Tag {
  id: string;
  slug: string;
  name: string;
  category: string | null;
  description: string | null;
  active: boolean;
  created_at: Date;
  updated_at: Date;
}

export interface ContentForReview {
  id: string;
  language: string;
  title: string;
  author: string | null;
  category: string | null;
  summary: string | null;
  body: string | null; // Full content body (HTML/markdown)
  published_at: string | null;
  revisions: ContentRevision[];
  created_at: string;
  tags: Array<{ id: string; name: string; slug: string; source?: TagSource }>;
  // Classification fields
  theme: ContentTheme | null;
  theme_suggested: string | null;
  nature: ContentNature | null;
  depth: ContentDepth | null;
  timeliness: ContentTimeliness | null;
  originality: ContentOriginality | null;
  // Quality signals
  authority_score: number | null;
  density_score: number | null;
  // Subject extraction
  primary_subject: SubjectEntity | null;
  mentions: SubjectEntity[];
  // Classification confidence
  classification_confidence: ClassificationConfidence;
  // Content metadata
  content_separator: string | null;
  // Content moderation
  approval_status: ApprovalStatus;
  flagged: boolean;
}

export interface ContentListItem {
  id: string;
  language: string;
  title: string;
  author: string | null;
  category: string | null;
  published_at: string | null;
  created_at: string;
  revision_count: number;
  has_human_review: boolean;
  avg_confidence: number | null;
  approval_status: ApprovalStatus;
  flagged: boolean;
  newsletter_send_id: string | null;
}

export interface NewsletterSendPreview {
  id: string;
  subject: string;
  sent_at: string;
  subscriber_count: number;
  items: Array<{
    id: string;
    title: string;
    summary: string | null;
    category: string | null;
    language: string;
  }>;
}

export interface ContentListResponse {
  items: ContentListItem[];
  total: number;
}

export interface UpdateContentDto {
  title?: string;
  author?: string;
  category?: string;
  summary?: string;
  tags?: string[]; // tag IDs
  // Classification fields
  theme?: ContentTheme;
  nature?: ContentNature;
  depth?: ContentDepth;
  timeliness?: ContentTimeliness;
  originality?: ContentOriginality;
}

export type ReviewStatus = 'pending' | 'reviewed' | 'flagged' | 'newsletter' | 'all';
export type PeriodFilter = '24h' | '7d' | '30d' | 'all';

export interface AdminComment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  user_name: string;
}

export interface ListContentParams {
  status?: ReviewStatus;
  language?: string;
  period?: PeriodFilter;
  minConfidence?: number;
  flagged?: boolean;
  newsletter?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export const adminService = {
  // List content for review
  async listContent(params?: ListContentParams): Promise<ContentListResponse> {
    return apiClient.get<ContentListResponse>(
      '/admin/content',
      params as Record<string, string | number | boolean | undefined>,
    );
  },

  // Get content details for review
  async getContent(id: string): Promise<ContentForReview> {
    return apiClient.get<ContentForReview>(`/admin/content/${id}`);
  },

  // Update content with human review
  async updateContent(id: string, updates: UpdateContentDto): Promise<ContentForReview> {
    return apiClient.patch<ContentForReview>(`/admin/content/${id}`, updates);
  },

  // Get available tags for selection
  async getTags(): Promise<Tag[]> {
    return apiClient.get<Tag[]>('/admin/content/tags');
  },

  // Approve content
  async approveContent(id: string): Promise<void> {
    return apiClient.post<void>(`/admin/content/${id}/approve`);
  },

  // Reject content
  async rejectContent(id: string): Promise<void> {
    return apiClient.post<void>(`/admin/content/${id}/reject`);
  },

  // Toggle flag status
  async toggleFlag(id: string): Promise<{ flagged: boolean }> {
    return apiClient.post<{ flagged: boolean }>(`/admin/content/${id}/flag`);
  },

  // Translate content to Brazilian Portuguese (creates a new pt content record)
  async translateContent(
    id: string,
  ): Promise<{ contentId: string; language: string; title: string; created: boolean }> {
    return apiClient.post<{
      contentId: string;
      language: string;
      title: string;
      created: boolean;
    }>(`/admin/content/${id}/translate`);
  },

  // Bulk approve content
  async bulkApprove(ids: string[]): Promise<{ updated: number }> {
    return apiClient.post<{ updated: number }>('/admin/content/bulk/approve', { ids });
  },

  // Bulk reject content
  async bulkReject(ids: string[]): Promise<{ updated: number }> {
    return apiClient.post<{ updated: number }>('/admin/content/bulk/reject', { ids });
  },

  // Admin comments
  async getAdminComments(contentId: string): Promise<AdminComment[]> {
    return apiClient.get<AdminComment[]>(`/admin/content/${contentId}/comments`);
  },

  async createAdminComment(contentId: string, content: string): Promise<AdminComment> {
    return apiClient.post<AdminComment>(`/admin/content/${contentId}/comments`, { content });
  },

  async deleteAdminComment(commentId: string): Promise<void> {
    return apiClient.delete(`/admin/comments/${commentId}`);
  },

  // Newsletter send preview
  async getNewsletterSendPreview(id: string): Promise<NewsletterSendPreview> {
    return apiClient.get<NewsletterSendPreview>(`/admin/newsletter-sends/${id}`);
  },
};
